import DateRangeSelector, {
    CustomDateRangeOption,
    DateRange,
} from '@/components/DateRangeSelector'
import SubHeaders from '@/components/Headers/SubHeaders'
import useTabs from '@/hooks/useTabs'
import AddIcons from '@/icons/AddIcons'
import DownLoadIcons from '@/icons/DownLoadIcons'
import { IPermissionProps } from '@/types/shared'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useTourHandler } from '@/contexts/tour-handler'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Tabs } from 'vnetwork-ui'
import List from './List'
import Statistical from './Statistical'
import { tabs } from './tabs'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useTicketHomeTour } from '@/modules/Ticket/hooks'
dayjs.extend(isoWeek)

// Custom options for DateRangeSelector
const customDateOptions: CustomDateRangeOption[] = [
    {
        value: 'this_week',
        label: 'date_range.this_week',
        calculate: () => ({
            gte: dayjs().startOf('isoWeek').format('YYYY-MM-DD'),
            lte: dayjs().endOf('isoWeek').format('YYYY-MM-DD'),
        }),
        detect: (range) => {
            const now = dayjs()
            return (
                range.gte === now.startOf('isoWeek').format('YYYY-MM-DD') &&
                range.lte === now.endOf('isoWeek').format('YYYY-MM-DD')
            )
        },
    },
    {
        value: 'this_year',
        label: 'date_range.this_year',
        calculate: () => ({
            gte: dayjs().startOf('year').format('YYYY-MM-DD'),
            lte: dayjs().endOf('year').format('YYYY-MM-DD'),
        }),
        detect: (range) => {
            const now = dayjs()
            return (
                range.gte === now.startOf('year').format('YYYY-MM-DD') &&
                range.lte === now.endOf('year').format('YYYY-MM-DD')
            )
        },
    },
]

const Home: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { startTour } = useTicketHomeTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler for keyboard shortcut (Ctrl+Shift+H or ?)
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Get date range from URL or use default (this year)
    const defaultGte = dayjs().startOf('year').format('YYYY-MM-DD')
    const defaultLte = dayjs().endOf('year').format('YYYY-MM-DD')

    const dateRange: DateRange = {
        gte: searchParams.get('gte') || defaultGte,
        lte: searchParams.get('lte') || defaultLte,
    }

    // Update URL when date range changes
    const setDateRange = useCallback(
        (newRange: DateRange) => {
            setSearchParams(
                (prev) => {
                    prev.set('gte', newRange.gte)
                    prev.set('lte', newRange.lte)
                    return prev
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    // Convert DateRange to [Dayjs, Dayjs] for compatibility with existing components
    const filterTime: [Dayjs | null, Dayjs | null] = [
        dateRange.gte ? dayjs(dateRange.gte) : null,
        dateRange.lte ? dayjs(dateRange.lte) : null,
    ]

    const { TabProps } = useTabs({
        tabs: tabs(t, filterTime),
    })
    const activeTab = TabProps.activeTab
    const exportChartToPDF = async (): Promise<void> => {
        window.print()
    }
    const { mutateAsync } = useMutationWithHandler<void, Error>(
        { mutationFn: exportChartToPDF },
        {
            reactQueryKeys: QUERY_KEYS.TICKETS,
            showSuccessToast: false,
            showErrorToast: false,
        }
    )
    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.ticket,
                        title: t('ticket-list'),
                    },
                ]}
            />

            <div className="lg:flex lg:flex-wrap items-center gap-4 mt-1 mb-2 bg-white rounded-md justify-between">
                <div data-tour="ticket-tabs">
                    <Tabs {...TabProps} tabContainerClassName="pt-0" />
                </div>

                <div className="flex items-center gap-2 justify-end">
                    <div data-tour="ticket-date-range">
                        <DateRangeSelector
                            value={dateRange}
                            onChange={setDateRange}
                            customOptions={customDateOptions}
                            excludeOptions={['yesterday', 'last_30_days']}
                        />
                    </div>
                    {activeTab === 'statistical' && (
                        <div data-tour="ticket-export-data">
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<DownLoadIcons />}
                                onClick={() => mutateAsync()}
                            >
                                {t('ticket-export-data')}
                            </Button>
                        </div>
                    )}
                    <div data-tour="ticket-create">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcons />}
                            onClick={() =>
                                navigate(
                                    generatePathDatacentral(
                                        paths.ticketCreate,
                                        {}
                                    )
                                )
                            }
                        >
                            {t('ticket-create')}
                        </Button>
                    </div>
                </div>
            </div>

            {activeTab === 'list' && <List filterTime={filterTime} />}
            {activeTab === 'statistical' && (
                <Statistical filterTime={filterTime} />
            )}
        </div>
    )
}
export default Home
